// src/components/attendance/AttendanceCard.jsx
import React, { useState, useEffect } from "react";
import {
  useTodayAttendance,
  useDocument,
  useIsEmployeeOnLeave,
  useTodayHoliday,
  formatTime,
} from "../../hooks/useFirestore";
import {
  useEmployeeShift,
  useAllShiftAssignments,
} from "../../hooks/useFirestoreExtensions";
import { useShiftValidation } from "../../hooks/useShiftValidation";
import { useAttendance } from "../../context/AttendanceContext";
import { useNotifications } from "../../context/NotificationContext";
import { doc, updateDoc, serverTimestamp, Timestamp } from "firebase/firestore";
import { db } from "../../firebase/config";
import "./AttendanceCard.css";

// ── Helpers ───────────────────────────────────────────────
function fmt12(time24) {
  if (!time24) return "—";
  const [h, m] = time24.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
}

function cleanShiftName(name, start, end) {
  if (!name) return "";
  const lower = name.toLowerCase();
  if (lower.startsWith("gen") && !lower.startsWith("general")) {
    return "General Shift";
  }
  if (start && end) {
    const sClean = start.replace(/\s*[AP]M\s*/gi, "").trim();
    const eClean = end.replace(/\s*[AP]M\s*/gi, "").trim();
    if (
      (sClean === "09:00" || sClean === "9:00") &&
      (eClean === "18:00" || eClean === "6:00" || eClean === "06:00") &&
      name !== "Admin Working Hours"
    ) {
      return "General Shift";
    }
  }
  return name;
}

function shiftHours(startTime, endTime) {
  if (!startTime || !endTime) return 8;
  const [sh, sm] = startTime.split(":").map(Number);
  const [eh, em] = endTime.split(":").map(Number);
  let mins = eh * 60 + em - (sh * 60 + sm);
  if (mins < 0) mins += 1440;
  return Math.round((mins / 60) * 10) / 10;
}

function calcHours(from, to) {
  return (to - from) / 3_600_000;
}

function formatHM(decimalHours) {
  if (!decimalHours || decimalHours <= 0) return "0h 0m 0s";
  const totalSeconds = Math.round(decimalHours * 3600);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return `${h}h ${m}m ${s}s`;
}


const SHIFT_TYPE_ICON = {
  morning: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      className="size-6"
      width="20"
      height="20"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 3v2.25m6.364.386-1.591 1.591M21 12h-2.25m-.386 6.364-1.591-1.591M12 18.75V21m-4.773-4.227-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0Z"
      />
    </svg>
  ),
  evening: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth="1.5"
      stroke="currentColor"
      className="size-6"
      style={{
        width: "1rem",
        height: "1rem",
      }}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M2.25 15a4.5 4.5 0 0 0 4.5 4.5H18a3.75 3.75 0 0 0 1.332-7.257 3 3 0 0 0-3.758-3.848 5.25 5.25 0 0 0-10.233 2.33A4.502 4.502 0 0 0 2.25 15Z"
      />
    </svg>
  ),
  night: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth="1.5"
      stroke="currentColor"
      className="size-6"
      style={{
        width: "1rem",
        height: "1rem",
      }}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M21.752 15.002A9.72 9.72 0 0 1 18 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 0 0 3 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 0 0 9.002-5.998Z"
      />
    </svg>
  ),
  general: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      className="size-6"
      style={{
        width: "1rem",
        height: "1rem",
      }}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
      />
    </svg>
  ),
  custom: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      style={{ width: "22px", height: "22px" }}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
      />
    </svg>
  ),
};

export default function AttendanceCard({ userId, userProfile }) {
  // ── Attendance data ──────────────────────────────────
  const {
    todayAttendance: allTodayAttendance,
    todayLoading,
    checkIn,
    checkOut,
    startBreak,
    endBreak,
  } = useAttendance();
  const { addNotification } = useNotifications();
  const todayAtt = allTodayAttendance.find((att) => att.userId === userId);

  // ── Shift data ───────────────────────────────────────────
  const { data: shiftAssignment, loading: saLoad } = useEmployeeShift(userId);

  // Also check if there's a pending request
  // We'll use useAllShiftAssignments and filter locally for simplicity in this component.
  const { data: assignments, loading: assignmentsLoad } =
    useAllShiftAssignments();
  const pendingRequest = assignments.find(
    (a) => a.userId === userId && a.status === "PENDING_ADMIN",
  );

  const { data: shiftDoc, loading: sdLoad } = useDocument(
    "shifts",
    shiftAssignment?.shiftId || null,
  );

  const isAdmin = userProfile?.role === "admin";
  const hasApprovedShift =
    shiftAssignment && shiftAssignment.status === "APPROVED";

  let useDefaultShift = false;
  let shiftStartTime = "09:00";
  let shiftEndTime = "18:00";
  let shiftName = "Assigned Shift";
  let shiftType = "morning";

  if (!hasApprovedShift) {
    useDefaultShift = true;
    shiftStartTime = "09:00";
    shiftEndTime = "18:00";
    shiftName = isAdmin ? "Admin Working Hours" : "General Shift";
    shiftType = "general";
  } else {
    // Initialize shift details from assignment, fallback to shift doc, then defaults
    shiftStartTime = shiftAssignment?.shiftStart || shiftDoc?.startTime || "09:00";
    shiftEndTime = shiftAssignment?.shiftEnd || shiftDoc?.endTime || "18:00";
    shiftName = shiftAssignment?.shiftName || shiftDoc?.name || "Assigned Shift";
    // Derive shift type, but force 'general' for any shift whose name includes 'general'
    const baseShiftType = shiftAssignment?.shiftType || shiftDoc?.type || (shiftAssignment?.shiftId ? shiftAssignment.shiftId.replace("-shift", "") : "custom");
    shiftType = shiftName.toLowerCase().includes("general") ? "general" : baseShiftType;
    // Override for standard General Shift timings (09:00-18:00)
    if (shiftStartTime === "09:00" && shiftEndTime === "18:00") {
      shiftName = "General Shift";
      shiftType = "general";
    }
  }

  const shiftDuration = shiftHours(shiftStartTime, shiftEndTime);
  const shiftLoading = saLoad || sdLoad || assignmentsLoad;

  // ── Local state ──────────────────────────────────────────
  const [mode, setMode] = useState("office");
  const [checking, setChecking] = useState(false);
  const [attErr, setAttErr] = useState("");
  const [breakStarting, setBreakStarting] = useState("");
  const [breakElapsed, setBreakElapsed] = useState(0);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const shiftStatus = useShiftValidation(shiftStartTime, shiftEndTime);

  const { data: isOnLeave } = useIsEmployeeOnLeave(userId);
  const { data: isHoliday } = useTodayHoliday();


  // ── Break timer logic ────────────────────────────────────
  useEffect(() => {
    let interval;
    const activeBreak = todayAtt?.breaks?.find((b) => !b.endTime);

    if (activeBreak?.startTime) {
      const start = activeBreak.startTime.toDate
        ? activeBreak.startTime.toDate()
        : new Date(activeBreak.startTime);

      setBreakElapsed(Math.floor((Date.now() - start.getTime()) / 1000));

      interval = setInterval(() => {
        setBreakElapsed((prev) => prev + 1);
      }, 1000);
    } else {
      setBreakElapsed(0);
    }

    return () => clearInterval(interval);
  }, [todayAtt?.breaks]);

  // ── Live hours worked, Working timer & Auto Checkout ───────────────────────
  const ENABLE_AUTO_CHECKOUT = false;
  const [liveHours, setLiveHours] = useState(0);

  async function handleAutoCheckOut(shiftEnd) {
    if (checking || !todayAtt || todayAtt.checkOut) return;
    setChecking(true);
    setAttErr("");
    try {
      const nowTs = Timestamp.fromDate(shiftEnd);
      const msWorked = shiftEnd - todayAtt.checkIn.toDate();
      let hoursWorked = Math.round((msWorked / 3_600_000) * 100) / 100;
      if (hoursWorked < 0) hoursWorked = 0;

      let updatedBreaks = [...(todayAtt.breaks || [])];
      const activeBreakIdx = updatedBreaks.findIndex((b) => !b.endTime);
      if (activeBreakIdx !== -1) {
        const activeBreak = updatedBreaks[activeBreakIdx];
        const breakStart = activeBreak.startTime.toDate();
        const endBreakTime = shiftEnd < breakStart ? breakStart : shiftEnd;
        const msDuration = endBreakTime - breakStart;
        const durationMins = Math.round(msDuration / 60000);
        updatedBreaks[activeBreakIdx] = {
          ...activeBreak,
          endTime: Timestamp.fromDate(endBreakTime),
          duration: durationMins > 0 ? durationMins : 0,
        };
      }

      await updateDoc(doc(db, "attendance", todayAtt.id), {
        breaks: updatedBreaks,
        checkOut: nowTs,
        hoursWorked,
        status: "AUTO-CHECKED-OUT",
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      console.error("Auto checkout failed:", err.message);
    }
    setChecking(false);
  }

  useEffect(() => {
    if (!todayAtt?.checkIn || todayAtt?.checkOut) {
      setLiveHours(todayAtt?.hoursWorked || 0);
      return;
    }

    // Calculate completed break milliseconds
    const completedBreakMs = (todayAtt?.breaks || []).reduce((sum, b) => {
      if (b.startTime && b.endTime) {
        const s = b.startTime.toDate ? b.startTime.toDate() : new Date(b.startTime);
        const e = b.endTime.toDate ? b.endTime.toDate() : new Date(b.endTime);
        return sum + Math.max(0, e - s);
      }
      return sum;
    }, 0);

    const start = todayAtt.checkIn.toDate ? todayAtt.checkIn.toDate() : new Date(todayAtt.checkIn);
    const compute = () => {
      const now = new Date();
      const grossMs = now.getTime() - start.getTime();
      const activeBreakMs = breakElapsed * 1000; // from break timer state
      const netMs = Math.max(0, grossMs - completedBreakMs - activeBreakMs);

      // Calculate shiftEnd relative to checkInDate for auto-checkout
      const [sh, sm] = shiftStartTime.split(":").map(Number);
      const [eh, em] = shiftEndTime.split(":").map(Number);
      const shiftEnd = new Date(start);
      shiftEnd.setHours(eh, em, 0, 0);
      if (eh < sh || (eh === sh && em < sm)) {
        shiftEnd.setDate(shiftEnd.getDate() + 1);
      }

      if (now > shiftEnd && ENABLE_AUTO_CHECKOUT) {
        handleAutoCheckOut(shiftEnd);
      } else {
        setLiveHours(netMs / 3_600_000);
      }
    };
    compute();
    const interval = setInterval(compute, 1000);
    return () => clearInterval(interval);
  }, [todayAtt?.checkIn, todayAtt?.checkOut, todayAtt?.breaks, breakElapsed]);

  // ── Handlers ─────────────────────────────────────────────
  async function handleCheckIn() {
    setAttErr("");
    if (userProfile?.isActive === false) {
      setAttErr("Your account is currently inactive. Please contact admin.");
      return;
    }
    if (!isAdmin && !shiftStatus.isWithinWindow) {
      setAttErr(
        useDefaultShift
          ? "Attendance available only during General Shift hours (09:00 AM - 06:00 PM)."
          : "Attendance available only during your assigned shift.",
      );
      return;
    }
    if (isOnLeave) {
      setAttErr("Cannot check in: You are on approved leave today.");
      return;
    }
    if (isHoliday) {
      setAttErr("Cannot check in: Today is a declared holiday.");
      return;
    }

    setChecking(true);
    try {
      await checkIn(userProfile, mode, shiftStartTime);
    } catch (err) {
      console.error("Check-in validation failed:", err.message);
      setAttErr(err.message);
    }
    setChecking(false);
  }

  async function handleCheckOut() {
    setAttErr("");
    if (userProfile?.isActive === false) {
      setAttErr("Account inactive. Action blocked.");
      return;
    }
    setChecking(true);
    try {
      await checkOut(todayAtt.id, todayAtt.checkIn);

      // Notify Team Lead/Admin
      if (userProfile.teamId || userProfile.managerId) {
        const leadId = userProfile.teamId || userProfile.managerId;
        await addNotification({
          type: "info",
          title: "Employee Check-Out",
          message: `${userProfile.displayName} checked out at ${new Date().toLocaleTimeString('en-IN')}`,
          recipientId: leadId,
        });
      }
    } catch (err) {
      console.error("Check-out validation failed:", err.message);
      setAttErr(err.message);
    }
    setChecking(false);
  }

  async function handleStartBreak(breakType) {
    setAttErr("");
    if (userProfile?.isActive === false) {
      setAttErr("Account inactive.");
      return;
    }

    setBreakStarting(breakType);
    try {
      await startBreak(todayAtt.id, breakType);
    } catch (err) {
      console.error("Break start validation failed:", err.message);
      setAttErr(err.message);
    }
    setBreakStarting("");
  }

  async function handleEndBreak() {
    setAttErr("");
    if (userProfile?.isActive === false) {
      setAttErr("Account inactive.");
      return;
    }

    try {
      await endBreak(todayAtt.id);
    } catch (err) {
      console.error("Break end validation failed:", err.message);
      setAttErr(err.message);
    }
  }

  const activeBreak = todayAtt?.breaks?.find((b) => !b.endTime);

  // Completed breaks: total break ms and total exceeded ms
  let completedBreakMs = 0;
  let totalExceededSecs = 0;

  (todayAtt?.breaks || []).forEach((b) => {
    if (b.startTime && b.endTime) {
      const s = b.startTime.toDate ? b.startTime.toDate() : new Date(b.startTime);
      const e = b.endTime.toDate ? b.endTime.toDate() : new Date(b.endTime);
      const actualMs = Math.max(0, e - s);
      completedBreakMs += actualMs;
      const allowed = (b.allowedMinutes ?? (b.breakType === "lunch" ? 60 : 15)) * 60000;
      totalExceededSecs += Math.max(0, (actualMs - allowed) / 1000);
    }
  });

  // Active break adds its live elapsed time
  const activeBreakMs = breakElapsed > 0 ? breakElapsed * 1000 : 0;
  const totalBreakHours = (completedBreakMs + activeBreakMs) / 3_600_000;

  // Exceeded during active break
  if (activeBreak && breakElapsed > 0) {
    const allowed = (activeBreak.allowedMinutes ?? (activeBreak.breakType === "lunch" ? 60 : 15)) * 60;
    totalExceededSecs += Math.max(0, breakElapsed - allowed);
  }

  // Working time = liveHours (already excludes break time via the new useEffect)
  let actualWorkingHours = 0;
  if (todayAtt?.checkOut) {
    actualWorkingHours = Math.max(0, todayAtt.hoursWorked || 0);
  } else if (todayAtt?.checkIn) {
    actualWorkingHours = Math.max(0, liveHours);
  }

  // Format exceeded break time for display
  const exceededMins = Math.floor(totalExceededSecs / 60);
  const exceededSecs = Math.floor(totalExceededSecs % 60);
  const exceededDisplay = totalExceededSecs > 0
    ? `+${exceededMins}m ${exceededSecs}s`
    : "0m";

  const progressPct = Math.min((actualWorkingHours / shiftDuration) * 100, 100);

  // Early / late indicator
  const checkInTime = todayAtt?.checkIn?.toDate?.();
  const shiftThreshold = checkInTime
    ? (() => {
      const t = new Date(checkInTime);
      const [h, m] = shiftStartTime.split(":").map(Number);
      t.setHours(h, m, 0, 0);
      return t;
    })()
    : null;

  if (todayLoading || shiftLoading) {
    return (
      <div className="dashboard-card attendance-widget">
        <div className="card-loading">Loading Attendance…</div>
      </div>
    );
  }





  return (
    <div className="dashboard-card attendance-widget">
      <div className="card-header">
        <h3 className="card-title">Today's Attendance</h3>
        <span className="live-clock">{shiftStatus.currentTimeStr}</span>
      </div>

      <div className="attendance-widget-content">
        {/* Validation handled silently; button disabled when unavailable */}

        {/* Shift Info Banner */}
        <div className="shift-info-banner compact-banner">
          <span className="banner-icon">
            {SHIFT_TYPE_ICON[shiftType?.toLowerCase()] || SHIFT_TYPE_ICON.custom}
          </span>
          <div className="banner-content">
            <span className="banner-title">
              {shiftName}
              {pendingRequest && !hasApprovedShift && " (Pending Request)"}
            </span>
            <span className="banner-subtitle">
              {fmt12(shiftStartTime)} → {fmt12(shiftEndTime)} · {shiftDuration}h
            </span>
          </div>
          {todayAtt?.checkIn && shiftThreshold && (
            <span
              className={`status-pill status-pill--${todayAtt.isLate ? "red" : "green"}`}
              style={{ flexShrink: 0 }}
            >
              {todayAtt.isLate ? `+${todayAtt.lateByMinutes}m late` : "On time"}
            </span>
          )}
        </div>

        {/* Mode toggle (before check-in) */}
        {!todayAtt && (
          <div className="mode-toggle">
            <button
              className={`mode-btn ${mode === "office" ? "mode-btn--active" : ""}`}
              onClick={() => setMode("office")}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                className="size-6"
                style={{
                  width: "1rem",
                  height: "1rem",
                  marginRight: "0.4rem",
                  verticalAlign: "middle",
                }}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008Zm0 3h.008v.008h-.008v-.008Zm0 3h.008v.008h-.008v-.008Z"
                />
              </svg>
              Office
            </button>

            <button
              className={`mode-btn ${mode === "remote" ? "mode-btn--active" : ""}`}
              onClick={() => setMode("remote")}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                className="size-6"
                style={{
                  width: "1rem",
                  height: "1rem",
                  marginRight: "0.4rem",
                  verticalAlign: "middle",
                }}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="m2.25 12 8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25"
                />
              </svg>
              Remote
            </button>
          </div>
        )}

        {/* Stats grid */}
        <div className="attendance-status-grid">
          <div className="att-stat">
            <span className="att-stat-label">Check In</span>
            <span className="att-stat-value">
              {todayAtt ? formatTime(todayAtt.checkIn) : "—"}
            </span>
          </div>
          <div className="att-stat">
            <span className="att-stat-label">Check Out</span>
            <span className="att-stat-value">
              {todayAtt?.checkOut ? formatTime(todayAtt.checkOut) : "—"}
            </span>
          </div>
          <div className="att-stat">
            <span className="att-stat-label">Work Time</span>
            <span className="att-stat-value">
              {actualWorkingHours > 0 ? formatHM(actualWorkingHours) : "0h 0m 0s"}
            </span>
          </div>
          <div className="att-stat">
            <span className="att-stat-label">Break Time</span>
            <span className="att-stat-value">
              {totalBreakHours > 0 ? formatHM(totalBreakHours) : "0h 0m 0s"}
            </span>
          </div>
          <div className="att-stat">
            <span className="att-stat-label">Exceeded Break</span>
            <span className="att-stat-value" style={{ color: totalExceededSecs > 0 ? "var(--red, #ef4444)" : "inherit" }}>
              {exceededDisplay}
            </span>
          </div>
          <div className="att-stat">
            <span className="att-stat-label">Mode</span>
            <span
              className="att-stat-value"
              style={{ textTransform: "capitalize" }}
            >
              {todayAtt?.mode || mode}
            </span>
          </div>
        </div>

        {/* Shift progress bar */}
        {todayAtt && (
          <div className="progress-section">
            <div className="hours-progress">
              <div
                className="hours-progress-bar"
                style={{
                  width: `${progressPct}%`,
                  background:
                    progressPct >= 100 ? "var(--green)" : "var(--brand-accent)",
                }}
              />
            </div>
            <div className="progress-footer">
              <span className="hours-progress-label">
                {formatHM(actualWorkingHours)} / {shiftDuration}h shift
              </span>
              {!todayAtt.checkOut && (
                <span className="expected-out">
                  End: <strong>{fmt12(shiftEndTime)}</strong>
                </span>
              )}
            </div>
          </div>
        )}

        {/* Active break indicator */}
        {activeBreak &&
          (() => {
            const maxMins = activeBreak.breakType === "lunch" ? 60 : 15;
            const elapsedMin = Math.floor(breakElapsed / 60);
            const elapsedSec = breakElapsed % 60;
            const overLimit = elapsedMin >= maxMins;
            return (
              <div
                className={`active-break-card ${overLimit ? "over-limit" : ""}`}
              >
                <div className="break-info">
                  <span className="break-name">
                    {activeBreak.breakType === "lunch" ? "🍽️" : "☕"}{" "}
                    {activeBreak.breakType === "tea1"
                      ? "Tea 1"
                      : activeBreak.breakType === "tea2"
                        ? "Tea 2"
                        : "Lunch"}
                  </span>
                  <span className="break-timer-val">
                    {String(elapsedMin).padStart(2, "0")}:
                    {String(elapsedSec).padStart(2, "0")} <span style={{ fontSize: "0.8em", opacity: 0.8 }}>/ {maxMins}:00</span>
                  </span>
                </div>
                <div className="break-progress-bg">
                  <div
                    className="break-progress-fill"
                    style={{
                      width: `${Math.min((elapsedMin / maxMins) * 100, 100)}%`,
                      background: overLimit ? "var(--red)" : "var(--amber)",
                    }}
                  />
                </div>
                <div style={{ marginTop: "0.5rem", fontSize: "0.8rem", color: overLimit ? "var(--red)" : "var(--amber)", fontWeight: 600, display: "flex", justifyContent: "space-between" }}>
                  <span>{overLimit ? `Exceeded by ${elapsedMin - maxMins} min` : "Within Limit"}</span>
                  {overLimit && <span>Excess: {elapsedMin - maxMins} min (Deducted)</span>}
                </div>
              </div>
            );
          })()}

        {/* Break buttons */}
        {todayAtt && !todayAtt.checkOut && (
          <div className="break-section">
            <div className="break-grid">
              {[
                {
                  key: "lunch",
                  label: (
                    <>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          textAlign: "center",
                          lineHeight: "1.2",
                          width: "100%",
                        }}
                      >
                        <div>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            style={{
                              marginRight: "6px",
                              verticalAlign: "middle",
                            }}
                          >
                            <path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2" />
                            <path d="M7 2v20" />
                            <path d="M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7" />
                          </svg>
                          Lunch
                        </div>
                        <small
                          style={{
                            fontSize: "11px",
                            color: "#666",
                            marginTop: "3px",
                            whiteSpace: "nowrap",
                          }}
                        >
                          1:00 PM - 2:00 PM
                        </small>
                      </div>
                    </>
                  ),
                },
              ].map(({ key, label }) => {
                const alreadyUsed = (todayAtt?.breaks || []).some(
                  (b) => b.breakType === key && b.endTime,
                );
                const isThisActive = activeBreak?.breakType === key;
                const isDisabled =
                  alreadyUsed ||
                  !!activeBreak ||
                  breakStarting === key ||
                  isOnLeave ||
                  isHoliday;
                return (
                  <button
                    key={key}
                    className={`btn-break-item ${isThisActive ? "active" : ""}`}
                    onClick={() => !isThisActive && handleStartBreak(key)}
                    disabled={isDisabled || userProfile?.isActive === false}
                    title={
                      userProfile?.isActive === false
                        ? "Account inactive"
                        : alreadyUsed
                          ? `${key === "lunch" ? "Lunch" : key === "tea1" ? "Tea 1" : "Tea 2"} taken`
                          : !!activeBreak && !isThisActive
                            ? "End current break first"
                            : ""
                    }
                  >
                    {breakStarting === key
                      ? "…"
                      : alreadyUsed
                        ? "✓"
                        : isThisActive
                          ? "Act."
                          : label}
                  </button>
                );
              })}
            </div>
          </div>
        )}


        {/* End break button */}
        {activeBreak && (
          <button
            className="btn-end-break-action"
            onClick={handleEndBreak}
            disabled={userProfile?.isActive === false}
          >
            ✓ End Break
          </button>
        )}

        {/* Check-in / Check-out actions */}
        <div className="att-actions">
          {!todayAtt ? (
            <button
              className="btn-checkin"
              onClick={handleCheckIn}
              disabled={
                checking ||
                isOnLeave ||
                isHoliday ||
                shiftLoading ||
                (!isAdmin && !shiftStatus.isWithinWindow) ||
                userProfile?.isActive === false
              }
              title={
                userProfile?.isActive === false
                  ? "Account inactive"
                  : isOnLeave
                    ? "On leave today"
                    : isHoliday
                      ? "Today is a holiday"
                      : shiftLoading
                        ? "Loading shift..."
                        : !isAdmin && !shiftStatus.isWithinWindow
                          ? shiftStatus.isBeforeStart
                            ? "Check-in window has not opened yet."
                            : "Shift window has passed."
                          : `Check in starts at ${fmt12(shiftStartTime)}`
              }
            >
              {checking ? (
                "Checking in…"
              ) : shiftLoading ? (
                "Loading…"
              ) : (
                <>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="size-6"
                    style={{
                      width: "1rem",
                      height: "1rem",
                      marginRight: "0.35rem",
                      verticalAlign: "middle",
                    }}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M8.25 9V5.25A2.25 2.25 0 0 1 10.5 3h6a2.25 2.25 0 0 1 2.25 2.25v13.5A2.25 2.25 0 0 1 16.5 21h-6a2.25 2.25 0 0 1-2.25-2.25V15M12 9l3 3m0 0-3 3m3-3H2.25"
                    />
                  </svg>
                  Check In
                  {shiftName ? ` (${fmt12(shiftStartTime)})` : ""}
                </>
              )}
            </button>
          ) : !todayAtt.checkOut ? (
            <button
              className="btn-checkout"
              onClick={() => setShowCheckoutModal(true)}
              disabled={
                checking || !!activeBreak || userProfile?.isActive === false
              }
              title={
                userProfile?.isActive === false
                  ? "Account inactive"
                  : activeBreak
                    ? "End break first"
                    : "Check out from today's attendance"
              }
            >
              {

                checking ? (
                  "Checking out…"
                ) : (
                  <>
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "8px",
                        fontSize: "16px",
                        fontWeight: "600",
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.8}
                        stroke="currentColor"
                        width="22"
                        height="22"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15m3 0 3-3m0 0-3-3m3 3H9"
                        />
                      </svg>

                      <span>
                        Check Out
                        {shiftName ? ` (${fmt12(shiftEndTime)})` : ""}
                      </span>
                    </span>
                  </>
                )
              }
            </button>
          ) : (
            <div className="att-done">
              ✅ Attendance recorded — {liveHours.toFixed(1)}h of{" "}
              {shiftDuration}h shift
              {todayAtt.isLate && (
                <span className="late-badge">
                  Late by {todayAtt.lateByMinutes}m
                </span>
              )}
            </div>
          )}
        </div>
        {showCheckoutModal && (
          <div className="modal-overlay">
            <div className="confirm-modal">
              <h3>Confirm Check Out</h3>

              <p>
                Are you sure you want to Check Out for today?
              </p>

              <div className="modal-buttons">
                <button
                  className="btn-secondary"
                  onClick={() => setShowCheckoutModal(false)}
                >
                  Cancel
                </button>

                <button
                  className="btn-primary"
                  onClick={() => {
                    setShowCheckoutModal(false);
                    handleCheckOut();
                  }}
                >
                  Check Out
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
